import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Loader2, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { deleteEmailTemplate, getEmailTemplates } from '../../Services/EmailAdminService';
import { EmailTemplate, EmailTemplateStatus } from '../../Types/EmailAdminType';
import EmailTabNav from './EmailTabNav';
import EmailTemplateEditor from './EmailTemplateEditor';

const STATUS_STYLES: Record<EmailTemplateStatus, string> = {
  ACTIVE: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  DRAFT: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  INACTIVE: 'border-slate-700 bg-slate-800/60 text-slate-400',
};

const PAGE_SIZE = 10;

function EmailTemplatesComponent() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const showAlert = useCallback(
    (variant: AlertProps['variant'], title: string, message: string) =>
      setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) }),
    []
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEmailTemplates({
        search: appliedSearch || undefined,
        status: statusFilter || undefined,
        page,
        size: PAGE_SIZE,
      });
      if (res.success && res.data) {
        const data = res.data as unknown as PaginationResponse<EmailTemplate>;
        setTemplates(data.content ?? []);
        setTotalPages(data.totalPages ?? 0);
        setTotalElements(data.totalElements ?? 0);
      } else {
        showAlert('error', 'Load failed', res.message || 'Could not load templates.');
      }
    } catch {
      showAlert('error', 'Load failed', 'Could not load templates.');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, statusFilter, page, showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applySearch = () => {
    setPage(0);
    setAppliedSearch(search.trim());
  };

  const handleDelete = async (template: EmailTemplate) => {
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    const res = await deleteEmailTemplate(template.id);
    if (res.success) {
      showAlert('success', 'Deleted', 'Template removed.');
      if (templates.length === 1 && page > 0) setPage((p) => p - 1);
      else fetchData();
    } else {
      showAlert('error', 'Delete failed', res.message || 'Please try again.');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (template: EmailTemplate) => {
    setEditing(template);
    setEditorOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <EmailTabNav />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-amber-500/30 bg-amber-500/10 text-amber-400">
          <FileText size={22} />
        </span>
        <div className="mr-auto">
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
            Email Configuration
          </h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            HTML templates stored in the database and rendered with {'{{token}}'} substitution
          </p>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2.5 text-xs font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:brightness-110"
        >
          <Plus size={14} />
          New Template
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            placeholder="Search by name or subject"
            className="w-full rounded-xl border border-slate-800 bg-slate-950/60 py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setPage(0);
            setStatusFilter(e.target.value);
          }}
          className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50 [color-scheme:dark]"
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="DRAFT">DRAFT</option>
          <option value="INACTIVE">INACTIVE</option>
        </select>
        <button
          onClick={applySearch}
          className="rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-cyan-400 transition hover:bg-cyan-500/20"
        >
          Search
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3 font-black">Name</th>
                <th className="px-4 py-3 font-black">Subject</th>
                <th className="px-4 py-3 font-black">Category</th>
                <th className="px-4 py-3 font-black">Vars</th>
                <th className="px-4 py-3 font-black">Status</th>
                <th className="px-4 py-3 text-right font-black">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center">
                    <Loader2 className="mx-auto animate-spin text-cyan-500" />
                  </td>
                </tr>
              )}
              {!loading && templates.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm font-semibold text-slate-500">
                    No templates yet. Create your first one.
                  </td>
                </tr>
              )}
              {!loading &&
                templates.map((t) => (
                  <motion.tr
                    key={t.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-slate-800/60 last:border-0 hover:bg-slate-900"
                  >
                    <td className="px-4 py-3 font-bold text-slate-100">{t.name}</td>
                    <td className="max-w-[240px] truncate px-4 py-3 text-slate-400">{t.subject || '—'}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-500">{t.category || '—'}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-400">
                      {t.variables?.length ?? 0}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-lg border px-2 py-1 text-[10px] font-black uppercase tracking-wider ${STATUS_STYLES[t.status] ?? STATUS_STYLES.INACTIVE}`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(t)}
                          className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(t)}
                          className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-red-500/40 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-800 px-4 py-3 text-xs font-semibold text-slate-500">
          <span>
            {totalElements} template{totalElements === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-1.5 font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              {totalPages === 0 ? 0 : page + 1} / {totalPages}
            </span>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-1.5 font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editorOpen && (
        <EmailTemplateEditor
          template={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={(msg) => {
            showAlert('success', 'Saved', msg);
            fetchData();
          }}
        />
      )}

      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}
    </div>
  );
}

export default EmailTemplatesComponent;
